import { AttachMoney, MoreVert } from '@mui/icons-material';
import { styled, Typography } from '@mui/material';
import PageSection from 'components/new/PageSection';
import { ActionOperationType, ActionRef } from 'further-types/lp/action';
import { ActionRefLabels } from 'further-ui/labels';
import {
  CalendarDay,
  furtherCased,
  numberToCurrencyString,
} from 'further-ui/utils';
import { CurrencyCode } from 'further-types/lp/vehicle';
import Alert from 'further-ui/components/Alert';
import { Card, CardContent } from 'further-ui/components/Card';
import { Column, Divider, Row } from 'further-ui/components/Layout';
import Menu from 'further-ui/components/Menu';

import { round, sumBy } from 'lodash';

const FullHeightCard = styled(Card)({
  height: '100%',
});

const FullHeightCardContent = styled(CardContent)(({ theme }) => ({
  height: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(theme.spacingSizes.sm),
}));

const CardTop = styled(Column)({
  flex: 1,
});

type Props = {
  bankOperationAmountTcy: number;
  selectedTransactions: Array<{
    id: string;
    splitFromTransactionId?: string;
    unreconciledAmountTcy: number;
    operationType: ActionOperationType;
    currencyCode: CurrencyCode;
    actionRef: ActionRef;
    ledgerDate: CalendarDay;
  }>;
  currency: CurrencyCode;
  bankFeeAmount: number;
  showBankFeeForm: boolean;
  onToggleBankFeeForm: (initialAmount: number) => void;
};

const SingleCurrencyReview: React.FC<Props> = ({
  selectedTransactions,
  bankOperationAmountTcy,
  currency,
  bankFeeAmount,
  showBankFeeForm,
  onToggleBankFeeForm,
}) => {
  const sumOfSelectedTransactions = round(
    sumBy(selectedTransactions, (transaction) =>
      transaction.operationType === ActionOperationType.Spent
        ? -transaction.unreconciledAmountTcy
        : transaction.unreconciledAmountTcy,
    ) +
      bankFeeAmount * Math.sign(bankOperationAmountTcy),
    3,
  );

  const amountMatches = sumOfSelectedTransactions === bankOperationAmountTcy;
  const amountDiff = Math.abs(
    sumOfSelectedTransactions - bankOperationAmountTcy,
  );

  return (
    <PageSection
      withoutBottomDivider
      title={
        <Row centered justify="space-between">
          1. Review selected transactions
          {!showBankFeeForm && (
            <Menu
              label="Quick add"
              handleIcon={MoreVert}
              items={[
                {
                  label: 'Bank fee',
                  icon: AttachMoney,
                  onClick: () => onToggleBankFeeForm(Math.max(0, amountDiff)),
                },
              ]}
            />
          )}
        </Row>
      }
    >
      <Row spacing="md" fullWidth breakpoint="md">
        <Column fullWidth>
          <FullHeightCard variant="outlined">
            <FullHeightCardContent size="sm">
              <CardTop fullWidth spacing="sm">
                <Typography variant="h6">Bank statement line</Typography>
                <Divider />
                <Row fullWidth justify="space-between">
                  <Typography variant="body2">Bank statement amount</Typography>
                  <Typography variant="body2">
                    {numberToCurrencyString(bankOperationAmountTcy, {
                      fallback: '-',
                      negativeInBrackets: true,
                      currency,
                    })}
                  </Typography>
                </Row>
              </CardTop>
              <Column fullWidth spacing="sm">
                <Divider />
                <Row fullWidth justify="space-between">
                  <Typography variant="h6" fontWeight="bold">
                    Total
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {numberToCurrencyString(bankOperationAmountTcy, {
                      fallback: '-',
                      negativeInBrackets: true,
                      currency,
                    })}
                  </Typography>
                </Row>
              </Column>
            </FullHeightCardContent>
          </FullHeightCard>
        </Column>

        <Column fullWidth>
          <Card variant="outlined">
            <CardContent size="sm">
              <Column fullWidth spacing="sm">
                <Typography variant="h6">
                  {bankFeeAmount > 0
                    ? 'Selected transactions and bank fees'
                    : 'Selected transactions'}
                </Typography>
                <Divider />
                {selectedTransactions.map((transaction) => (
                  <Row key={transaction.id} fullWidth justify="space-between">
                    <Typography variant="body2">
                      {furtherCased(ActionRefLabels[transaction.actionRef])} on{' '}
                      {transaction.ledgerDate.toReadable()}
                    </Typography>
                    <Typography variant="body2">
                      {numberToCurrencyString(
                        transaction.unreconciledAmountTcy,
                        {
                          fallback: '-',
                          negativeInBrackets: true,
                          currency,
                        },
                      )}
                    </Typography>
                  </Row>
                ))}
                {bankFeeAmount > 0 && (
                  <Row fullWidth justify="space-between">
                    <Typography variant="body2">Bank fee</Typography>
                    <Typography variant="body2">
                      {numberToCurrencyString(bankFeeAmount, {
                        fallback: '-',
                        negativeInBrackets: true,
                        currency,
                      })}
                    </Typography>
                  </Row>
                )}
                <Divider />
                <Row fullWidth justify="space-between">
                  <Typography variant="h6" fontWeight="bold">
                    Total
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {numberToCurrencyString(sumOfSelectedTransactions, {
                      fallback: '-',
                      negativeInBrackets: true,
                      currency,
                    })}
                  </Typography>
                </Row>
              </Column>
            </CardContent>
          </Card>
        </Column>
      </Row>
      <Alert severity={amountMatches ? 'success' : 'error'}>
        {amountMatches
          ? 'Statement and transaction total matches'
          : `Statement and transaction total has a ${numberToCurrencyString(
              amountDiff,
              { currency },
            )} mismatch`}
      </Alert>
    </PageSection>
  );
};

export default SingleCurrencyReview;
